import store_accueil from '@/store/mods/clinique/accueil'
import store_rendez_vous from '@/store/mods/clinique/rendez_vous'
import { aujourdhui, jour_lisible, heure_rdv, date_lisible, heure_lisible } from '@/utils/rendez-vous'

// KPI-001 : cartes « Consultations » du tableau de bord Accueil/Caisse.
// En attente = arrivées non démarrées ; Aujourd'hui et Mois = consultations DÉMARRÉES (date_debut),
// toutes cliquables (tableaux de détail). Les rendez-vous viennent de l'agenda existant.
const MOIS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
const STATS_CACHE_MS = 60000
const RDV_CACHE_MS = 30000

const deux = n => (n < 10 ? '0' : '') + n

export default {
    name: 'activite-consultations',
    data() {
        const maintenant = new Date()
        const mois_courant = maintenant.getFullYear() + '-' + deux(maintenant.getMonth() + 1)
        return {
            stats_loading: true,
            stats_has_data: false,
            stats_error: '',
            stats: { waiting: 0, waiting_women: 0, waiting_men: 0, waiting_children: 0, waiting_unclassified: 0, started_today: 0, started_month: 0, breakdown_today: [], breakdown_month: [], month: mois_courant, today: '', timezone: '' },
            mois_selectionne: mois_courant,
            rdv_date: aujourdhui(),
            rdv_total: 0,
            rdv_loading: true,
            rdv_has_data: false,
            rdv_error: '',
            // Fenêtre de détail : 'waiting' | 'today' | 'month' | 'rdv' | null
            modal: null,
            waiting_group: 'all',
            onglet_detail: 'specialites',
            stats_sequence: 0,
            liste_sequence: 0,
            liste: [],
            liste_page: 1,
            liste_pages: 1,
            liste_total: 0,
            liste_loading: false,
            liste_error: ''
        }
    },
    computed: {
        mois_label() {
            const morceaux = String(this.mois_selectionne).split('-')
            return morceaux.length === 2 ? MOIS_FR[Number(morceaux[1]) - 1] + ' ' + morceaux[0] : this.mois_selectionne
        },
        // Les 24 derniers mois restent accessibles, y compris après un changement d'année.
        mois_disponibles() {
            const maintenant = new Date(), liste = []
            for (let index = 0; index < 24; index++) {
                const date = new Date(maintenant.getFullYear(), maintenant.getMonth() - index, 1)
                liste.push({ french: MOIS_FR[date.getMonth()] + ' ' + date.getFullYear(), value: date.getFullYear() + '-' + deux(date.getMonth() + 1) })
            }
            return liste
        },
        repartition_active() {
            return this.stats[this.modal === 'today' ? 'breakdown_today' : 'breakdown_month'] || []
        },
        recap_specialites() {
            const par_specialite = {}
            this.repartition_active.forEach((ligne) => {
                const nom = ligne.type_consultation || 'Non renseignée'
                if (!par_specialite[nom]) par_specialite[nom] = { type_consultation: nom, total: 0, patients: 0, medecins: [] }
                par_specialite[nom].total += Number(ligne.total) || 0
                par_specialite[nom].patients += Number(ligne.patients) || 0
                if (ligne.medecin_nom) par_specialite[nom].medecins.push(ligne.medecin_nom + ' (' + ligne.total + ')')
            })
            return Object.values(par_specialite)
        },
        recap_specialistes() {
            const par_medecin = {}
            this.repartition_active.forEach((ligne) => {
                const nom = ligne.medecin_nom || 'Non attribué'
                const cle = String(ligne.doctor_id || 0) + '-' + nom
                if (!par_medecin[cle]) par_medecin[cle] = { nom: nom, total: 0, patients: 0, specialites: [] }
                par_medecin[cle].total += Number(ligne.total) || 0
                par_medecin[cle].patients += Number(ligne.patients) || 0
                if (ligne.type_consultation) par_medecin[cle].specialites.push(ligne.type_consultation)
            })
            return Object.values(par_medecin)
        },
        detail_total() {
            if (this.modal === 'waiting') return this.stats.waiting
            if (this.modal === 'rdv') return this.rdv_total
            return this.stats[this.modal === 'today' ? 'started_today' : 'started_month']
        },
        detail_patients() { return this.stats[this.modal === 'today' ? 'patient_count_today' : 'patient_count_month'] || 0 },
        detail_medecins() { return this.stats[this.modal === 'today' ? 'doctor_count_today' : 'doctor_count_month'] || 0 },
        modal_titre() {
            switch (this.modal) {
                case 'waiting': return 'Consultations en attente'
                case 'today': return 'Consultations · Aujourd\'hui'
                case 'month': return 'Consultations · ' + this.mois_label
                case 'rdv': return 'Rendez-vous du ' + jour_lisible(this.rdv_date)
                default: return ''
            }
        }
    },
    mounted() {
        this.restaurer_stats_cache()
        this.restaurer_rdv_cache()
        this.charger_stats()
        this.charger_rdv()
    },
    beforeDestroy() {
        this.stats_sequence++
        this.liste_sequence++
    },
    methods: {
        cache_key(suffix) {
            const auth = this.$store.getters['authInfo'] || {}
            const user = auth.user || {}
            const role = this.$store.getters['activeRole'] || {}
            if (!user.id || !role.nom) return null
            return 'clinicaa:accueil-kpi:' + String(user.id) + ':' + String(role.nom) + ':' + suffix
        },
        lire_cache(suffix, duree) {
            try {
                const cle = this.cache_key(suffix)
                if (!cle) return null
                const brut = sessionStorage.getItem(cle)
                const entree = brut ? JSON.parse(brut) : null
                return entree && Date.now() - entree.enregistre_le < duree ? entree.valeur : null
            } catch (error) { return null }
        },
        ecrire_cache(suffix, valeur) {
            try {
                const cle = this.cache_key(suffix)
                if (cle) sessionStorage.setItem(cle, JSON.stringify({ enregistre_le: Date.now(), valeur }))
            }
            catch (error) { /* Le cache est facultatif (navigation privée, quota). */ }
        },
        restaurer_stats_cache() {
            const cache = this.lire_cache('stats:' + this.mois_selectionne, STATS_CACHE_MS)
            if (!cache || cache.month !== this.mois_selectionne) return
            this.stats = Object.assign(this.stats, cache)
            this.stats_has_data = true
        },
        restaurer_rdv_cache() {
            const cache = this.lire_cache('rdv:' + this.rdv_date, RDV_CACHE_MS)
            if (!cache || cache.date !== this.rdv_date) return
            this.rdv_total = cache.total
            this.rdv_has_data = true
        },
        charger_stats(details = false) {
            const sequence = ++this.stats_sequence
            this.stats_loading = true
            this.stats_error = ''
            store_accueil.getStatistiques({ month: this.mois_selectionne, summary_only: !details }).then((stats) => {
                if (sequence !== this.stats_sequence) return
                this.stats = Object.assign(this.stats, stats)
                this.stats_has_data = true
                this.ecrire_cache('stats:' + this.mois_selectionne, {
                    month: stats.month, waiting: stats.waiting,
                    started_today: stats.started_today, started_month: stats.started_month
                })
                this.stats_loading = false
            }).catch(() => {
                if (sequence !== this.stats_sequence) return
                this.stats_error = 'Indicateurs indisponibles.'
                this.stats_loading = false
            })
        },
        charger_rdv() {
            this.rdv_loading = true
            this.rdv_error = ''
            store_rendez_vous.getAgenda({ periode: 'jour', date: this.rdv_date, page: 1, per_page: 1 }).then((result) => {
                this.rdv_total = Number(result.total) || 0
                this.rdv_has_data = true
                this.ecrire_cache('rdv:' + this.rdv_date, { date: this.rdv_date, total: this.rdv_total })
                this.rdv_loading = false
            }).catch(() => {
                this.rdv_error = 'Rendez-vous indisponibles.'
                this.rdv_loading = false
            })
        },
        changer_mois() {
            this.restaurer_stats_cache()
            this.charger_stats(this.modal === 'month')
            if (this.modal === 'month') this.charger_liste(1)
        },
        changer_date_rdv() {
            if (!this.rdv_date) this.rdv_date = aujourdhui()
            this.rdv_has_data = false
            this.restaurer_rdv_cache()
            this.charger_rdv()
        },
        actualiser() {
            this.charger_stats()
            this.charger_rdv()
        },
        // Ouverture d'une carte : charge la première page du tableau correspondant
        ouvrir(carte) {
            this.modal = carte
            this.waiting_group = 'all'
            if (carte !== 'rdv') this.charger_stats(true)
            this.onglet_detail = carte === 'today' || carte === 'month' ? 'specialites' : 'patients'
            this.liste = []
            this.liste_page = 1
            this.liste_pages = 1
            this.liste_total = 0
            this.charger_liste(1)
        },
        choisir_onglet(onglet) {
            this.onglet_detail = onglet
        },
        filtrer_attente(groupe) {
            if (this.modal !== 'waiting' || this.waiting_group === groupe) return
            this.waiting_group = groupe
            this.charger_liste(1)
        },
        fermer() {
            this.modal = null
            this.liste_sequence++
            this.liste = []
            this.liste_error = ''
        },
        charger_liste(page) {
            const sequence = ++this.liste_sequence
            this.liste_loading = true
            this.liste_error = ''
            this.liste = []
            this.liste_total = 0
            if (this.modal === 'rdv') {
                store_rendez_vous.getAgenda({ periode: 'jour', date: this.rdv_date, page: page, per_page: 15 }).then((result) => {
                    if (sequence !== this.liste_sequence) return
                    this.liste = result.data || []
                    this.liste_page = Number(result.current_page) || page
                    this.liste_pages = Math.max(1, Number(result.last_page) || 1)
                    this.liste_total = Number(result.total) || 0
                    this.liste_loading = false
                }).catch(() => {
                    if (sequence !== this.liste_sequence) return
                    this.liste = []
                    this.liste_error = 'Liste indisponible.'
                    this.liste_loading = false
                })
                return
            }
            const scope = this.modal === 'waiting' ? 'waiting' : (this.modal === 'today' ? 'started_today' : 'started_month')
            const parametres = { scope: scope, page: page }
            if (scope === 'waiting') parametres.waiting_group = this.waiting_group
            if (scope === 'started_month') parametres.month = this.mois_selectionne
            store_accueil.getTableau(parametres).then((result) => {
                if (sequence !== this.liste_sequence) return
                this.liste = result.data || []
                this.liste_page = Number(result.current_page) || page
                this.liste_pages = Math.max(1, Number(result.last_page) || 1)
                this.liste_total = Number(result.total) || 0
                this.liste_loading = false
            }).catch(() => {
                if (sequence !== this.liste_sequence) return
                this.liste = []
                this.liste_error = 'Liste indisponible.'
                this.liste_loading = false
            })
        },
        page_precedente() { if (this.liste_page > 1) this.charger_liste(this.liste_page - 1) },
        page_suivante() { if (this.liste_page < this.liste_pages) this.charger_liste(this.liste_page + 1) },
        // Affichage : les instants ISO du serveur sont lus tels quels (fuseau de la clinique, jamais celui du poste)
        date_heure(iso) { return iso ? date_lisible(String(iso).replace('T', ' ').substr(0, 16)) : '—' },
        heure(iso) { return iso ? heure_lisible(iso) : '—' },
        heure_rendez_vous(ligne) { return heure_rdv(ligne) },
        etat(ligne) {
            if (ligne.is_end) return 'Clôturée'
            if (ligne.is_start) return 'En cours'
            return 'En attente'
        },
        etat_classe(ligne) {
            if (ligne.is_end) return 'badge-success'
            if (ligne.is_start) return 'badge-primary'
            return 'badge-warning'
        }
    }
}
